import React, { useState, useEffect } from 'react';
import rolesApi from '../../api/rolesApi';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Box,
  Divider,
  AppBar,
  Toolbar,
  Slide
} from '@mui/material';
import { Add, Edit, Delete, Security, Sync, Close as CloseIcon } from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
import { toast } from 'react-toastify';

export default function RolesPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth(); // Access auth context
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const currentUserName = localStorage.getItem('userName') || "";

  // Check if user is authorized (Owner or has Role.Manage)
  const isAuthorized = currentUserName.toLowerCase() === 'owner' || hasPermission('Role.Manage') || hasPermission('Security.Manage');

  if (!isAuthorized) {
    return (
      <Box p={3} dir="rtl" textAlign="center" mt={5}>
        <Security sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" color="text.secondary" fontWeight="bold">
          عذراً، ليس لديك صلاحية للوصول إلى هذه الصفحة.
        </Typography>
        <Typography variant="body1" color="text.secondary" mt={1}>
          هذه الصفحة مخصصة للمدير العام (Owner) أو من يملك صلاحية إدارة الأدوار.
        </Typography>
      </Box>
    );
  }

  // Form State
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await rolesApi.getAll(companyId);
      setRoles(res.data);
    } catch (err) {
      toast.error('فشل تحميل الأدوار');
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await rolesApi.getPermissions();
      setPermissions(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  // Helper for translation
  const getArabicName = (perm) => {
    const parts = perm.split('.');
    const action = parts[parts.length - 1];
    
    const actionMap = {
      'Create': 'إنشاء',
      'Add': 'إضافة',
      'Edit': 'تعديل',
      'Update': 'تحديث',
      'Delete': 'حذف',
      'Remove': 'إزالة',
      'View': 'عرض',
      'Read': 'قراءة',
      'Print': 'طباعة',
      'Approve': 'اعتماد',
      'Reject': 'رفض',
      'Export': 'تصدير',
      'Import': 'استيراد',
      'Manage': 'إدارة',
      'Post': 'ترحيل',
      'Cancel': 'إلغاء'
    };
  
    if (actionMap[action]) return actionMap[action];
    return action; // Fallback
  };
  
  const getCategoryArabicName = (category) => {
      const categoryMap = {
          'Invoice': 'الفواتير',
          'Customer': 'العملاء',
          'Vendor': 'الموردين',
          'Bank': 'البنوك',
          'Bond': 'السندات',
          'Product': 'المنتجات',
          'Category': 'التصنيفات',
          'User': 'المستخدمين',
          'Role': 'الأدوار',
          'Report': 'التقارير',
          'Setting': 'الإعدادات',
          'GISettings': 'الإعدادات العامة',
          'ChartOfAccounts': 'شجرة الحسابات',
          'JournalEntry': 'القيود اليومية',
          'CostCenter': 'مراكز التكلفة',
          // Add composite categories
          'Invoice.Sales': 'فواتير المبيعات',
          'Invoice.Purchase': 'فواتير المشتريات',
          'Invoice.Quotation': 'عروض الأسعار',
          'Invoice.PurchaseReturn': 'مرتجع مشتريات',
          'Invoice.SalesReturn': 'مرتجع مبيعات',
          'Invoice.OpeningStock': 'رصيد افتتاحي',
          'Bond.Receipt': 'سندات القبض',
          'Bond.Payment': 'سندات الصرف',
          'Bond.Tax': 'سند ضريبة',
      };
      return categoryMap[category] || category;
  }

  const [newPermission, setNewPermission] = useState('');

  const handleAddCustomPermission = () => {
    if (!newPermission.trim()) return;
    const perm = newPermission.trim();
    
    // Add to local list of all permissions if not exists
    if (!permissions.includes(perm)) {
      setPermissions(prev => [...prev, perm]);
    }

    // Automatically check/select it for the current role
    if (!formData.permissions.includes(perm)) {
      setFormData(prev => ({ ...prev, permissions: [...prev.permissions, perm] }));
    }

    setNewPermission('');
    toast.success(`تم إضافة الصلاحية: ${perm}`);
  };

  const handleOpen = (role = null) => {
    if (role) {
      setEditingId(role.id);
      setFormData({
        name: role.name,
        description: role.description || '',
        permissions: role.permissions || []
      });
    } else {
      setEditingId(null);
      setFormData({ name: '', description: '', permissions: [] });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleTogglePermission = (perm) => {
    setFormData(prev => {
      const exists = prev.permissions.includes(perm);
      if (exists) {
        return { ...prev, permissions: prev.permissions.filter(p => p !== perm) };
      } else {
        return { ...prev, permissions: [...prev.permissions, perm] };
      }
    });
  };

  const handleSubmit = async () => {
    if (!formData.name) return toast.warning('يرجى إدخال اسم الدور');
    setLoading(true);
    try {
      if (editingId) {
        await rolesApi.update(editingId, { ...formData, companyId });
        toast.success('تم تحديث الدور بنجاح');
      } else {
        await rolesApi.create({ ...formData, companyId });
        toast.success('تم إنشاء الدور بنجاح');
      }
      setOpen(false);
      fetchRoles();
    } catch (err) {
      toast.error('حدث خطأ أثناء الحفظ');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الدور؟')) return;
    try {
      await rolesApi.delete(id);
      toast.success('تم الحذف بنجاح');
      fetchRoles();
    } catch (err) {
      toast.error('لا يمكن حذف هذا الدور (قد يكون مرتبطاً بمستخدمين)');
    }
  };

  const handleSyncPermissions = async () => {
    setSyncing(true);
    try {
      await rolesApi.syncPermissions();
      toast.success("تم تحديث الصلاحيات للأدوار النظامية بنجاح");
      fetchRoles(); // Refresh roles to see new counts
    } catch (err) {
      console.error(err);
      toast.error("فشل تحديث الصلاحيات");
    } finally {
      setSyncing(false);
    }
  };

  // Group permissions by category (e.g. "Invoice.Create" -> Category: Invoice)
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const parts = perm.split('.');
    let category = parts[0];
    
    // Group "Invoice.Sales", "Invoice.Purchase", etc. separately
    if ((category === 'Invoice' || category === 'Bond') && parts.length > 2) {
        category = `${parts[0]}.${parts[1]}`;
    }

    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {});
const handleSelectAllPermissions = () => {
  setFormData(prev => ({
    ...prev,
    permissions: [...permissions] // نسخ كل الصلاحيات
  }));
};

const handleClearAllPermissions = () => {
  setFormData(prev => ({
    ...prev,
    permissions: []
  }));
};

  return (
    <Box p={3} dir="rtl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
          <Security color="primary" /> إدارة الأدوار والصلاحيات
        </Typography>
        <Box display="flex" gap={2}>
            <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={syncing ? <Sync className="animate-spin"/> : <Sync />} 
                onClick={handleSyncPermissions}
                disabled={syncing}
            >
                {syncing ? "جاري التحديث..." : "تحديث صلاحيات النظام"}
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                إضافة دور جديد
            </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>اسم الدور</TableCell>
              <TableCell>الوصف</TableCell>
              <TableCell>عدد الصلاحيات</TableCell>
              <TableCell>إجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell fontWeight="bold">{role.name}</TableCell>
                <TableCell>{role.description || '-'}</TableCell>
                <TableCell>
                  <Chip label={`${role.permissions.length} صلاحية`} size="small" color="info" variant="outlined" />
                </TableCell>
                <TableCell>
                  {!role.isSystemRole && (
                    <>
                      <IconButton color="primary" onClick={() => handleOpen(role)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(role.id)}>
                        <Delete />
                      </IconButton>
                    </>
                  )}
                  {role.isSystemRole && <Chip label="نظام" size="small" />}
                </TableCell>
              </TableRow>
            ))}
            {roles.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">لا توجد أدوار مضافة</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        fullScreen 
        TransitionComponent={Transition}
        dir="rtl"
      >
        <AppBar sx={{ position: 'relative', bgcolor: 'primary.main' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {editingId ? 'تعديل الدور' : 'إضافة دور جديد'}
            </Typography>
            <Button autoFocus color="inherit" onClick={handleSubmit} disabled={loading}>
              {loading ? 'جاري الحفظ...' : 'حفظ'}
            </Button>
          </Toolbar>
        </AppBar>
        
        <DialogContent sx={{ bgcolor: '#f5f5f5', p: 4 }}>
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>بيانات الدور</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="اسم الدور"
                  fullWidth
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="الوصف"
                  fullWidth
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
               <Typography variant="h6" fontWeight="bold">إدارة الصلاحيات</Typography>
               <Box display="flex" gap={1}>
                  <Button variant="outlined" color="primary" onClick={handleSelectAllPermissions} size="small">
                    منح الكل
                  </Button>
                  <Button variant="outlined" color="error" onClick={handleClearAllPermissions} size="small">
                    إلغاء الكل
                  </Button>
               </Box>
            </Box>

            <Box display="flex" gap={2} mb={3} alignItems="center" bgcolor="#f8f9fa" p={2} borderRadius={2} border="1px dashed #ccc">
              <TextField
                  label="إضافة صلاحية جديدة يدوياً"
                  placeholder="مثال: Invoice.Type.Special"
                  size="small"
                  value={newPermission}
                  onChange={(e) => setNewPermission(e.target.value)}
                  sx={{ width: 300 }}
              />
              <Button 
                  variant="contained" 
                  color="secondary"
                  startIcon={<Add />} 
                  onClick={handleAddCustomPermission}
                  disabled={!newPermission.trim()}
              >
                  إضافة
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" mb={2}>
              تم تحديد ({formData.permissions.length}) من أصل ({permissions.length}) صلاحية
            </Typography>
            
            <Grid container spacing={2}>
            {Object.entries(groupedPermissions).map(([category, perms]) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={category}>
                <Paper 
                  elevation={0} 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    height: '100%', 
                    borderRadius: 2,
                    borderColor: '#eee',
                    '&:hover': { borderColor: 'primary.main', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
                    transition: 'all 0.2s'
                  }}
                >
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="bold" 
                    color="primary" 
                    align="center"
                    mb={2}
                    pb={1}
                    borderBottom="1px dashed #eee"
                  >
                    {getCategoryArabicName(category)}
                  </Typography>
                  
                  <Box display="flex" flexDirection="column" gap={1}>
                    {perms.map(perm => (
                        <Box 
                          key={perm} 
                          display="flex" 
                          justifyContent="space-between" 
                          alignItems="center"
                          sx={{ 
                            p: 0.5, 
                            borderRadius: 1, 
                            '&:hover': { bgcolor: '#f9f9f9' } 
                          }}
                        >
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {getArabicName(perm)}
                          </Typography>
                          <Switch
                            checked={formData.permissions.includes(perm)}
                            onChange={() => handleTogglePermission(perm)}
                            size="small"
                            color="primary"
                          />
                        </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
          </Paper>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
